import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  computed,
  ErrorHandler,
  inject,
  isDevMode,
  provideAppInitializer,
} from '@angular/core';
import {
  initializeAnalytics,
  provideAnalytics,
} from '@jfw/angular/shared/firebase/analytics';
import {
  getApp,
  initializeApp,
  provideFirebaseApp,
} from '@jfw/angular/shared/firebase/app';
import {
  initializeAppCheck,
  provideAppCheck,
  ReCaptchaEnterpriseProvider,
} from '@jfw/angular/shared/firebase/app-check';
import { getAuth, provideAuth } from '@jfw/angular/shared/firebase/auth';
import {
  getFirestore,
  provideFirestore,
} from '@jfw/angular/shared/firebase/firestore';
import {
  MAT_DATE_LOCALE,
  provideNativeDateAdapter,
} from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { PLATFORM_PORTAL_ENVIRONMENT } from '@jfw/angular/platform/tokens';
import { appCheckInterceptor } from '@jfw/angular/shared/app-check';
import {
  FEEDBACK_DIALOG_HOST,
  FeedbackActivityRecorder,
  FeedbackErrorHandler,
  FeedbackErrorInterceptor,
} from '@jfw/angular/shared/common-components/feedback';
import {
  LocaleDateSyncService,
  provideMaterialTranslations,
  provideSharedTranslocoScope,
  toDateLocale,
} from '@jfw/angular/shared/i18n';
import {
  IS_IT_DEPARTMENT_USER,
  PLATFORM_DP_ENVIRONMENT,
  SHARED_ENVIRONMENT,
} from '@jfw/angular/shared/tokens';
import { ModuleDepartmentEnum } from '@jfw/types';
import { provideTransloco } from '@jsverse/transloco';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { platformPortalTranslocoConfig } from './i18n/platform-portal-transloco-config';
import { providePlatformPortalTranslocoScope } from './i18n/platform-portal-transloco-scope';
import { TokenInterceptor } from './standalone/interceptors/token/token.interceptor';
import { AuthService } from './standalone/services/auth/auth.service';
import { PlatformFeedbackDialogHost } from './standalone/services/feedback/platform-feedback-dialog-host.service';
import { LanguagePreferenceService } from './standalone/services/language/language-preference.service';
import { ThemeService } from './standalone/services/theme/theme.service';
import { ReuseComponentRouteStrategy } from './standalone/services/routing/reuse-component-route.strategy';

//deploy 34

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    // EXT-6013: opt-in route detach/reattach for routes flagged with
    // data.reuseComponent (see the strategy's own doc comment) -- currently
    // only /wholesale/search.
    { provide: RouteReuseStrategy, useClass: ReuseComponentRouteStrategy },
    provideNativeDateAdapter(), // instead of importing MatNativeDateModule
    // EXT-5696: MAT_DATE_LOCALE only seeds the adapter once at construction;
    // LocaleDateSyncService (initialized below) keeps it in step with the
    // active Transloco language at runtime.
    { provide: MAT_DATE_LOCALE, useValue: toDateLocale('en') },
    // EXT-5701/5707: a missing or empty Spanish value shows its English text,
    // never a blank (see platformPortalTranslocoConfig and its spec).
    provideTransloco({ config: platformPortalTranslocoConfig(!isDevMode()) }),
    provideSharedTranslocoScope(),
    // EXT-5702: platform-portal's own `platformPortal.*` translations.
    providePlatformPortalTranslocoScope(),
    // EXT-6342: datepicker/paginator built-in labels follow the language too.
    provideMaterialTranslations(),
    provideAppInitializer(() => inject(LocaleDateSyncService).init()),
    // EXT-5700: applies the user's saved language before first render.
    provideAppInitializer(() => inject(LanguagePreferenceService).init()),
    // EXT-6562: the Light / Dark / System setting (index.html pre-paints it).
    provideAppInitializer(() => inject(ThemeService).init()),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    // EXT-5255: send_page_view: false suppresses gtag.js's own automatic
    // page_view (fired on init, before Angular's title is set) — page_view
    // is instead fired explicitly by PageViewTrackingService once the
    // router-resolved title is guaranteed correct.
    provideAnalytics(() =>
      initializeAnalytics(getApp(), { config: { send_page_view: false } }),
    ),
    provideAppCheck(() => {
      const key = environment.recaptchaAppCheckKey;
      if (!key) {
        if (isDevMode())
          alert('recaptchaAppCheckKey not defined in environment.ts');
        throw new Error('recaptchaAppCheckKey not defined in environment.ts');
      }
      return initializeAppCheck(getApp(), {
        provider: new ReCaptchaEnterpriseProvider(key),
        isTokenAutoRefreshEnabled: false, // this is also the default
      });
    }),
    // HTTP interceptor is used with the token interceptor to add the user's JWT to each outgoing request.
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    // EXT-6172: failed calls and uncaught errors feed the feedback dialog's
    // "recent errors" context. Both pass everything through unchanged.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FeedbackErrorInterceptor,
      multi: true,
    },
    { provide: ErrorHandler, useClass: FeedbackErrorHandler },
    // Constructed at startup rather than on first use, so route history
    // covers the pages visited before the feedback dialog is first opened.
    provideAppInitializer(() => {
      inject(FeedbackActivityRecorder);
    }),
    // EXT-6239: the shared dialog's portal-specific half.
    { provide: FEEDBACK_DIALOG_HOST, useExisting: PlatformFeedbackDialogHost },
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([appCheckInterceptor]),
    ),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: PLATFORM_PORTAL_ENVIRONMENT,
      useValue: environment,
    },
    {
      provide: PLATFORM_DP_ENVIRONMENT, // used by jfw-library components and services shared between Platform/Dealer Portal
      useValue: environment,
    },
    {
      provide: SHARED_ENVIRONMENT,
      useValue: environment,
    },
    {
      // EXT-5689: gates the dev-only order-comparison harness button to IT-department users —
      // same department.includes('IT') check business-logic's own doesUserHavePermission/
      // getUserModules use as their IT-bypass.
      provide: IS_IT_DEPARTMENT_USER,
      useFactory: () => {
        const authService = inject(AuthService);
        return computed(
          () =>
            !!authService
              .platformUser()
              ?.department.includes(ModuleDepartmentEnum.IT),
        );
      },
    },
  ],
};
